import type { GameRuleConstructor, GameRuleState } from "./GameRule";
import type { TerrainFeature, TerrainFeatureSet, TerrainFeatureSetup } from "./TerrainFeature";

export type SpearheadRealm = "aqshy" | "ghyran";

export const RuinedDomicileBell: TerrainFeature = {};
export const RavagedShrineBell: TerrainFeature = {};
export const RuinedDomicileBone: TerrainFeature = {};
export const RavagedShrineBone: TerrainFeature = {};

export const SpearheadTerrainFeatures = {
  ravaged_bell: RavagedShrineBell,
  ravaged_bone: RavagedShrineBone,
  ruined_bell:  RuinedDomicileBell,
  ruined_bone:  RuinedDomicileBone
} satisfies { [key: string]: TerrainFeature };
export type SpearheadTerrainFeatures = typeof SpearheadTerrainFeatures;

export type SpearheadTerrainFeatureId = keyof typeof SpearheadTerrainFeatures;
export const SpearheadTerrainFeatureId = Object.keys(SpearheadTerrainFeatures) as SpearheadTerrainFeatureId[];

export const BellTerrainFeature: TerrainFeatureSet = { terrainFeatures: ["ruined_bell", "ravaged_bell"] };
export const BoneTerrainFeature: TerrainFeatureSet = { terrainFeatures: ["ruined_bone", "ravaged_bone"] };

export const SpearheadTerrainFeatureSets = {
  bell: BellTerrainFeature,
  bone: BoneTerrainFeature
} satisfies { [key: string]: TerrainFeatureSet };
export type SpearheadTerrainFeatureSets = typeof SpearheadTerrainFeatureSets;

export type SpearheadTerrainFeatureSetId = keyof typeof SpearheadTerrainFeatureSets;
export const SpearheadTerrainFeatureSetId = Object.keys(SpearheadTerrainFeatureSets) as SpearheadTerrainFeatureSetId[];

export interface SpearheadGameRuleState extends GameRuleState {
  cards: (number | undefined)[];
  enhancements: number[];
  map: { horizontal: boolean; realm: SpearheadRealm; side: boolean };
  secret: { p1cards: number[]; p1drownCards: number[]; p2cards: number[]; p2drownCards: number[]; twistCards: number[] };
  terrainSets: SpearheadTerrainFeatureSetId[];
  twistCard?: number;
}

export const enhancementsArray = [0, 1, 2, 3];
export const checkEnhancement = (enhancement: number) => typeof enhancement === "number" && enhancement >= 0 && enhancement <= 3;
export const checkTerrainFeature = (terrainFeature: TerrainFeatureSetup) =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  typeof terrainFeature === "object" && terrainFeature && "id" in terrainFeature;

export interface TwistCard {
  eachPlayer?: boolean;
  rollOff?: boolean;
  underdog?: boolean;
}

export const TwistCards: { [key in SpearheadRealm]: TwistCard[] } = {
  aqshy: [
    { underdog: true },
    { underdog: true },
    { rollOff: true, underdog: true },
    { eachPlayer: true, underdog: true },
    { rollOff: true, underdog: true },
    { eachPlayer: true, underdog: true }
  ],
  ghyran: [
    { rollOff: true, underdog: true },
    { eachPlayer: true, underdog: true },
    { rollOff: true, underdog: true },
    {},
    { rollOff: true, underdog: true },
    { rollOff: true, underdog: true }
  ]
};

export const SpearheadGameRule = <S extends boolean, T extends SpearheadGameRuleState = SpearheadGameRuleState>(Base: GameRuleConstructor<S, T>) => {
  abstract class SpearheadGameRule extends Base {
    otherTerrainFeatures(terrainSets: SpearheadTerrainFeatureSetId[], other: number) {
      return SpearheadTerrainFeatureSets[terrainSets[other - 1]].terrainFeatures;
    }

    setEnhancements() {
      return enhancementsArray.some(_ => ! checkEnhancement(this.state.enhancements[_]));
    }

    setMap() {
      const { horizontal, realm, side } = this.state.map;

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return horizontal === undefined || realm === undefined || side === undefined;
    }

    setTerrainFeatures() {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return ! this.state.terrainFeatures || enhancementsArray.some(_ => ! checkTerrainFeature(this.state.terrainFeatures[_]));
    }
  }

  return SpearheadGameRule;
};
